import { Controller } from "@hotwired/stimulus"
import { setCookie, getCookie, deleteCookie } from "../lib/cookie_utils"

// Coordinates postal code and search query validation
// Always requires postal code - GPS is used to auto-fill it
export default class extends Controller {
  static targets = ["postalCode", "query", "submit", "hint", "locationStatus"]

  connect() {
    this.restorePostalCode()
    this.requestGpsLocation()
    this.updateButtonState()
  }

  restorePostalCode() {
    const saved = getCookie('selected_postal_code')
    if (saved && this.hasPostalCodeTarget) {
      this.postalCodeTarget.value = saved
      this.showLocationStatus("from_cookie", saved)
    }
  }

  requestGpsLocation() {
    // Skip GPS if we already have a postal code
    if (this.isPostalCodeValid()) {
      return
    }

    if (!navigator.geolocation) {
      this.showLocationStatus("enter_postal")
      return
    }

    this.showLocationStatus("detecting")

    navigator.geolocation.getCurrentPosition(
      (position) => this.onGpsSuccess(position),
      (error) => this.onGpsError(error),
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000 // Cache for 5 minutes
      }
    )
  }

  async onGpsSuccess(position) {
    const lat = position.coords.latitude
    const lng = position.coords.longitude

    // Reverse geocode to get postal code
    try {
      const response = await fetch(`/api/geocoding/reverse?lat=${lat}&lng=${lng}`)
      const data = await response.json()

      if (data.postal_code) {
        // Fill the postal code input (this will trigger cookie save via input event)
        if (this.hasPostalCodeTarget) {
          this.postalCodeTarget.value = data.postal_code
          // Manually save cookie and update state since we're setting value programmatically
          setCookie('selected_postal_code', data.postal_code, 365)
          this.showLocationStatus("detected", `${data.city}, ${data.state} (${data.postal_code})`)
          this.updateButtonState()
        }
      } else {
        this.showLocationStatus("enter_postal")
      }
    } catch (error) {
      console.log('Reverse geocode error:', error)
      this.showLocationStatus("enter_postal")
    }
  }

  onGpsError(error) {
    console.log('GPS error:', error.message)
    this.showLocationStatus("enter_postal")
  }

  showLocationStatus(status, locationText = null) {
    if (!this.hasLocationStatusTarget) return

    const statusMessages = {
      detecting: "📍 Detectando ubicación...",
      detected: `📍 ${locationText}`,
      from_cookie: `📍 CP: ${locationText}`,
      enter_postal: "Ingresa tu código postal"
    }

    this.locationStatusTarget.textContent = statusMessages[status] || status
  }

  onPostalCodeChange(event) {
    const value = event.target.value?.trim()

    if (/^\d{5}$/.test(value)) {
      setCookie('selected_postal_code', value, 365)
      this.showLocationStatus("from_cookie", value)
    } else {
      deleteCookie('selected_postal_code')
      this.showLocationStatus("enter_postal")
    }

    this.updateButtonState()
  }

  onQueryChange() {
    this.updateButtonState()
  }

  updateButtonState() {
    const postalValid = this.isPostalCodeValid()
    const queryValid = this.isQueryValid()
    const shouldEnable = postalValid && queryValid

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = !shouldEnable
    }

    if (this.hasHintTarget) {
      if (!shouldEnable) {
        if (!postalValid) {
          this.hintTarget.textContent = "Ingresa tu código postal para buscar"
        } else if (!queryValid) {
          this.hintTarget.textContent = "Ingresa el nombre del medicamento"
        }
        this.hintTarget.classList.remove('hidden')
      } else {
        this.hintTarget.classList.add('hidden')
      }
    }
  }

  isPostalCodeValid() {
    if (!this.hasPostalCodeTarget) return false
    const value = this.postalCodeTarget.value?.trim()
    return /^\d{5}$/.test(value)
  }

  isQueryValid() {
    if (!this.hasQueryTarget) return true
    const value = this.queryTarget.value?.trim()
    return value && value.length > 0
  }
}
